import {
  addPricesFiat,
  getLodgingFintechTotal,
} from "@apac/components/checkout";
import type { Prices } from "@b2bportal/purchase-api";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { getRbcLodgingPriceBreakdown } from "../PayAtProperty/getRbcLodgingPriceBreakdown";

/**
 * Add an ancillary (CFAR) premium to a room price for the displayed Total /
 * Amount due. Combines the FIAT amount only: a room price and an ancillary
 * premium carry different rewards `accountSpecific` account keys, so `addPrices`
 * (which requires identical account keys) cannot be used here — the room's
 * rewards/points are applied separately downstream. We still enforce currency
 * parity, the one guard `addPricesFiat` does not perform, so a future
 * non-user-preferred-currency path fails loud instead of silently adding e.g.
 * USD to CAD. All RBC lodging prices are emitted in the user-preferred currency,
 * so this invariant should always hold.
 */
const addAncillaryFiat = (
  base?: Prices,
  ancillary?: Prices
): Prices | undefined => {
  if (base == null || ancillary == null) {
    return addPricesFiat(base, ancillary);
  }
  if (base.fiat.currencyCode !== ancillary.fiat.currencyCode) {
    throw new Error(
      `Cannot add ancillary premium (${ancillary.fiat.currencyCode}) to a total in ${base.fiat.currencyCode}`
    );
  }
  return addPricesFiat(base, ancillary);
};

export interface UseHotelCheckoutTotalsReturn {
  /** Full trip total (room + taxes + fintech), for the "Total" line. */
  tripTotalWithFintech: Prices | undefined;
  /**
   * Amount charged to the card now (due-now + fintech), before rewards. This is
   * the pre-quote fallback base for the shared `remainingBalance` calculation,
   * so the price breakdown and the Avion earn agree even before a cart quote
   * exists.
   */
  dueNowTotalWithFintech: Prices | undefined;
}

/**
 * Single source for the CFAR-inclusive hotel totals. The pricingV2 /
 * customerPriceBreakdown is room-scoped and excludes ancillary premiums (CFAR),
 * so we fold the fintech products into the Total and Amount due the same way the
 * pre-pricingV2 breakdown did (RBC-2233). `getLodgingFintechTotal` resolves CFAR
 * from shop-selected OR quoted products, so it is correct pre- and post-quote.
 */
export const useHotelCheckoutTotals = (): UseHotelCheckoutTotalsReturn => {
  const breakdown = useCheckoutStateSelector(getRbcLodgingPriceBreakdown);
  const fintechTotal = useCheckoutStateSelector(getLodgingFintechTotal);

  return {
    tripTotalWithFintech: addAncillaryFiat(breakdown?.tripTotal, fintechTotal),
    dueNowTotalWithFintech: addAncillaryFiat(
      breakdown?.dueNowTotal,
      fintechTotal
    ),
  };
};
